import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Card, Tooltip, Typography } from "@mui/material";
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import FileSaver from "file-saver";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import LogisticCompoundAction, {
  ILCEparking,
  ILCFinancial,
  ILCSupplier,
  ILogisticCompound,
} from "../../service/logisticCompound.action";
import OperationTypes from "../../types";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { GenericTitleHeader } from "../common/GenericTitleHeader";
import { nameof } from "../common/ObjectUtils";
import EditLogisticCompoundModul from "./editLogisticCompoundModal";
import SearchLogisticCompoundModal from "./searchLogisticCompoundModal";
import { TypesOfData } from "./types";
import { saveFileFromResponse } from "../../utils";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EnumChangeName from "../common/EnumChangeName";
import SectionHeader from "../../../../util/component/SectionHeader";
import { useHasScope } from "../../../../config/globalConfig";

const LogisticCompoundTable = () => {
  const { setLoading } = useContext(ContextOperation);

  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const t = useOperationTranslation();
  const hasScope = useHasScope;

  const logisticCompoundAction = LogisticCompoundAction();
  const [searchData, setSearchData] = useState<TypesOfData.SearchFuncTypes>({});
  const [formData, setFormData] = useState<TypesOfData.SearchFuncTypes>({});
  const { tenant } = useContext(ContextOperation);
  // const [editData, setEditData] = useState<
  //   TypesOfData.EditFuncTypes | undefined
  // >(undefined);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const addTooltipTable = (name: string) => {
    return (
      <>
        <Tooltip title={name || ""}>
          <span>{name || ""}</span>
        </Tooltip>
      </>
    );
  };
  const columns: ColDef[] = [
    {
      field: nameof<ILogisticCompound>("name"),
      headerName: t("logisticCompound.table.name"),
      minWidth: 150,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: nameof<ILogisticCompound>("identificationCode"),
      headerName: t("logisticCompound.table.identificationCode"),
      sortable: true,
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("oldIdentificationCode"),
      headerName: t("logisticCompound.table.oldIdentificationCode"),
      sortable: true,
      minWidth: 270,
    },
    {
      field: nameof<ILogisticCompound>("bpCode"),
      headerName: t("logisticCompound.table.bpCode"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("bpKind"),
      headerName: t("logisticCompound.table.bpKind"),
      minWidth: 150,
      valueFormatter: (params: ValueFormatterParams<ILogisticCompound>) =>
        `${params.data?.bpKind.code} - ${params.data?.bpKind.description}`,
    },
    {
      field: nameof<ILogisticCompound>("currentStatus"),
      headerName: t("logisticCompound.table.currentStatus"),
      minWidth: 180,
      cellRenderer: (params: any) => {
        return EnumChangeName().activatTerminatType(
          params?.data?.currentStatus
        );
      },
    },
    {
      field: nameof<ILogisticCompound>("serviceType"),
      headerName: t("logisticCompound.table.serviceType"),
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        params.valueFormatted ? addTooltipTable(params.valueFormatted) : "",
      valueFormatter: (params: ValueFormatterParams<ILogisticCompound>) =>
        params.data?.serviceType
          ?.map((val) => `${val.code} - ${val.description}`)
          .join(" , ") || "",
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("fax"),
      headerName: t("logisticCompound.table.fax"),
      minWidth: 180,
    },
    {
      field: nameof<ILogisticCompound>("phoneNumber"),
      headerName: t("logisticCompound.table.phoneNumber"),
      minWidth: 200,
    },
    {
      field: nameof<ILogisticCompound>("cellPhoneNumber"),
      headerName: t("logisticCompound.table.cellPhoneNumber"),
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("email"),
      headerName: t("logisticCompound.table.email"),
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: nameof<ILogisticCompound>("currentAttribute"),
      headerName: t("logisticCompound.table.currentAttribute"),
      minWidth: 200,
    },
    {
      field: nameof<ILogisticCompound>("category"),
      headerName: t("logisticCompound.table.category"),
      valueFormatter: (params: ValueFormatterParams<ILogisticCompound>) =>
        params.data?.category
          ?.map((val) => `${val.code} - ${val.description}`)
          .join(" , ") || "",
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("city17"),
      headerName: t("logisticCompound.table.city17"),
      minWidth: 180,
    },
    {
      field: nameof<ILogisticCompound>("longitude"),
      headerName: t("logisticCompound.table.longitude"),
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("latitude"),
      headerName: t("logisticCompound.table.latitude"),
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("branchManager"),
      headerName: t("logisticCompound.table.branchManager"),
      minWidth: 200,
    },
    {
      field: nameof<ILogisticCompound>("branchManagerCellPhoneNumber"),
      headerName: t("logisticCompound.table.branchManagerCellPhoneNumber"),
      minWidth: 300,
    },
    {
      field: nameof<ILogisticCompound>("brandName"),
      headerName: t("logisticCompound.table.brandName"),
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("areaManager"),
      headerName: t("logisticCompound.table.areaManager"),
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("chargingSite"),
      headerName: t("logisticCompound.table.chargingSite"),
      minWidth: 150,
      cellRenderer: (props: ICellRendererParams<ILogisticCompound>) => {
        return (
          <span>
            {" "}
            {props.data?.chargingSite
              ? t("internOptionSelectName.true")
              : t("internOptionSelectName.false")}
          </span>
        );
      },
    },
    {
      field: nameof<ILogisticCompound>("deliveryCollection"),
      headerName: t("logisticCompound.table.deliveryCollection"),
      minWidth: 200,
    },
    {
      field: nameof<ILogisticCompound>("note"),
      headerName: t("logisticCompound.table.note"),
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: nameof<ILogisticCompound>("howToIta"),
      headerName: t("logisticCompound.table.howToIta"),
      minWidth: 150,
    },
    {
      field: nameof<ILogisticCompound>("howToEng"),
      headerName: t("logisticCompound.table.howToEng"),
      minWidth: 160,
    },
    {
      field: nameof<ILogisticCompound>("openingClosingHours"),
      headerName: t("logisticCompound.table.openingClosingHours"),
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("openingTimeMonFri"),
      headerName: t("logisticCompound.table.openingTimeMonFri"),
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("openingTimeSat"),
      headerName: t("logisticCompound.table.openingTimeSat"),
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("openingTimeSun"),
      headerName: t("logisticCompound.table.openingTimeSun"),
      minWidth: 250,
    },
    {
      field: nameof<ILogisticCompound>("as400Code"),
      headerName: t("logisticCompound.table.as400Code"),
      minWidth: 150,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "representativeAccountCode"
      )}`,
      headerName: t("logisticCompound.table.representativeAccountCode"),
      minWidth: 300,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "clientIdentificationCode"
      )}`,
      headerName: t("logisticCompound.table.clientIdentificationCode"),
      minWidth: 270,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "clientAccountCode"
      )}`,
      headerName: t("logisticCompound.table.clientAccountCode"),
      minWidth: 220,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "zone"
      )}`,
      headerName: t("logisticCompound.table.zone"),
      minWidth: 130,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "feesDued"
      )}`,
      headerName: t("logisticCompound.table.feesDued"),
      minWidth: 150,
      cellRenderer: (props: ICellRendererParams<ILCFinancial>) => {
        return (
          <span>
            {" "}
            {props.data?.feesDued
              ? t("internOptionSelectName.true")
              : t("internOptionSelectName.false")}
          </span>
        );
      },
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "rentalFiscalReceiptsCode"
      )}`,
      headerName: t("logisticCompound.table.rentalFiscalReceiptsCode"),
      minWidth: 300,
    },
    {
      field: `${nameof<ILogisticCompound>("financial")}.${nameof<ILCFinancial>(
        "feesFiscalReceiptsCode"
      )}`,
      headerName: t("logisticCompound.table.feesFiscalReceiptsCode"),
      minWidth: 340,
    },
    {
      field: `${nameof<ILogisticCompound>("supplier")}.${nameof<ILCSupplier>(
        "bpCode"
      )}`,
      headerName: t("logisticCompound.table.supplierBpCode"),
      minWidth: 200,
    },
    {
      field: `${nameof<ILogisticCompound>("supplier")}.${nameof<ILCSupplier>(
        "name"
      )}`,
      headerName: t("logisticCompound.table.supplierName"),
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: `${nameof<ILogisticCompound>("supplier")}.${nameof<ILCSupplier>(
        "vatNumber"
      )}`,
      headerName: t("logisticCompound.table.supplierVatNumber"),
      minWidth: 250,
    },
    {
      field: `${nameof<ILogisticCompound>("supplier")}.${nameof<ILCSupplier>(
        "taxCode"
      )}`,
      headerName: t("logisticCompound.table.supplierTaxCode"),
      minWidth: 250,
    },
    {
      field: `${nameof<ILogisticCompound>("supplier")}.${nameof<ILCSupplier>(
        "address"
      )}`,
      headerName: t("logisticCompound.table.supplierAddress"),
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: `${nameof<ILogisticCompound>(
        "location"
      )}.${nameof<OperationTypes.Domain>("address")}`,
      headerName: t("logisticCompound.table.locationAddress"),
      minWidth: 250,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "identificationCode"
      )}`,
      headerName: t("logisticCompound.table.eParkingIdentificationCode"),
      minWidth: 290,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "status"
      )}`,
      headerName: t("logisticCompound.table.eParkingStatus"),
      minWidth: 200,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "address"
      )}`,
      headerName: t("logisticCompound.table.eParkingAddress"),
      minWidth: 200,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "streetNumber"
      )}`,
      headerName: t("logisticCompound.table.eParkingStreetNumber"),
      minWidth: 250,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "certificationProtocolIdentificationCode"
      )}`,
      headerName: t("logisticCompound.table.eParkingCertProtIdCode"),
      minWidth: 500,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "evaluationOutcome"
      )}`,
      headerName: t("logisticCompound.table.eParkingEvaluationOutcome"),
      minWidth: 300,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "note"
      )}`,
      headerName: t("logisticCompound.table.eParkingNote"),
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) =>
        addTooltipTable(params.value),
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "requiredPower"
      )}`,
      headerName: t("logisticCompound.table.eParkingRequiredPower"),
      minWidth: 280,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "effectivePower"
      )}`,
      headerName: t("logisticCompound.table.eParkingEffectivePower"),
      minWidth: 260,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "chargingColumns"
      )}`,
      headerName: t("logisticCompound.table.eParkingChargingColumns"),
      minWidth: 270,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "chargingColumnId"
      )}`,
      headerName: t("logisticCompound.table.eParkingChargingColumnId"),
      minWidth: 320,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "chargingColumnType"
      )}`,
      headerName: t("logisticCompound.table.eParkingchargingColumnType"),
      minWidth: 350,
    },
    {
      field: `${nameof<ILogisticCompound>("eparking")}.${nameof<ILCEparking>(
        "chargingPoints"
      )}`,
      headerName: t("logisticCompound.table.eParkingChargingPoints"),
      minWidth: 250,
    },
    {
      field: "_actions",
      headerName: "",
      minWidth: 80,
      maxWidth: 200,
      pinned: "right",
      cellRenderer: (params: ICellRendererParams<ILogisticCompound>) => {
        return (
          <Box>
            {hasScope("SCOPE_UPDATE_MDSUPPLIER_LOGISTIC_COMPOUND") && (
            <Tooltip title={t("tooltipTitle.edit")}>
              <Button
                onClick={() => editShowDetails(params.data?.id)}
                variant="text"
                size="icon"
              >
                <EditIcon />
              </Button>
            </Tooltip>
            )}
            </Box>

        );
      },
    },
  ];

  const [datasource, _totalElements, refreshDatasource] =
    useAgGridCustomDatasource(
      logisticCompoundAction.getLogisticCompound,
      [tenant, formData],
      [tenant, formData]
    );

  const editShowDetails = (id: number | undefined) => {
    if (!id) {
      snackbar.show("warning", "No id found for this row!");
      return;
    }
    navigate(`/operation/suppliers/logisticCompounds/details/${id}`);
  };

  // const editLogisticCompoundData = (
  //   logisticCompound: ILogisticCompound | undefined
  // ) => {
  //   if (!logisticCompound) return;

  //   setEditData({
  //     currentStatus: !!logisticCompound.currentStatus
  //       ? logisticCompound.currentStatus
  //       : "",
  //     city17: !!logisticCompound.city17 ? logisticCompound.city17 : "",
  //     brandName: !!logisticCompound.brandName ? logisticCompound.brandName : "",
  //     chargingSite: logisticCompound.chargingSite,
  //     deliveryCollection: !!logisticCompound.deliveryCollection
  //       ? logisticCompound.deliveryCollection
  //       : "",
  //     note: !!logisticCompound.note ? logisticCompound.note : "",
  //     howToIta: !!logisticCompound.howToIta ? logisticCompound.howToIta : "",
  //     howToEng: !!logisticCompound.howToEng ? logisticCompound.howToEng : "",
  //     openingClosingHours: !!logisticCompound.openingClosingHours
  //       ? logisticCompound.openingClosingHours
  //       : "",
  //     openingTimeMonFri: !!logisticCompound.openingTimeMonFri
  //       ? logisticCompound.openingTimeMonFri
  //       : "",
  //     openingTimeSat: !!logisticCompound.openingTimeSat
  //       ? logisticCompound.openingTimeSat
  //       : "",
  //     openingTimeSun: !!logisticCompound.openingTimeSun
  //       ? logisticCompound.openingTimeSun
  //       : "",
  //     as400Code: !!logisticCompound.as400Code ? logisticCompound.as400Code : "",
  //     id: logisticCompound.id,
  //   });
  //   setOpenEditModal(true);
  // };

  // const updateLogisticCompoundData = (editValues: any) => {
  //   setLoading(true);

  //   logisticCompoundAction
  //     .editLogisticCompound(tenant, editValues)
  //     .then((response: any) => {
  //       refreshDatasource();

  //       snackbar.show(
  //         "success",
  //         t("logisticCompound.editModal.editActionSuccessMessage")
  //       );

  //       setLoading(false);
  //     })
  //     .catch((error: any) => {
  //       if (error.response) {
  //         snackbar.show("error", error.response.data.error);

  //         setLoading(false);
  //       } else if (error.request) {
  //         snackbar.show("error", t("networkErrorMessage"));

  //         setLoading(false);
  //       } else {
  //         setLoading(false);
  //       }
  //     });
  // };

  const exportLogisticCompound = () => {
    setLoading(true);
    logisticCompoundAction
      .exportLogisticCompound(tenant, formData)
      .then((response: any) => {
        saveFileFromResponse(response);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        snackbar.show("error", t("missingEquipment.errors.actionError"));
      });
  };

  const resetData = () => {
    setSearchData({});
    setFormData({});
  };

  return (
    <Box>
      <SectionHeader
          title={t("logisticCompound.titleFirst") + " " + t("logisticCompound.titleSecond")}
      />
      <SearchLogisticCompoundModal
        searchData={searchData}
        setSearchData={setSearchData}
        setFormData={setFormData}
        resetData={resetData}
        refreshTable={refreshDatasource}
      />
      <Box className="carBrandOuterTableBox">
        <Card>
          <Box
            minHeight={46}
            pr={1}
            display="flex"
            justifyContent={"flex-end"}
            alignItems="center"
          >
            <Button
              color="accent"
              variant="text"
              size="icon"
              disabled={_totalElements ? false : true}
              onClick={exportLogisticCompound}
            >
              <FileDownloadIcon />
            </Button>
          </Box>
        </Card>
        <Box>
           <AgGridOperationTable
            columnDefs={columns}
            serverSideDatasource={datasource}
            rowStyle={{ width: "1000%" }}
            totalElements={_totalElements}

            />

        </Box>
      </Box>

      {/* {openEditModal && (
        <EditLogisticCompoundModul
          editData={editData}
          setEditData={setEditData}
          openEditModal={openEditModal}
          setOpenEditModal={setOpenEditModal}
          updateLogisticCompoundData={updateLogisticCompoundData}
        />
      )} */}
    </Box>
  );
};

export default LogisticCompoundTable;
